import React, { useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion, useInView } from 'framer-motion'
import { useOptimizedScroll } from '../hooks/useOptimizedScroll'
import { useIsMobile } from '../hooks/useMediaQuery'
import { PageMobileFooter } from './layout/PageMobileFooter'
import { careersPageFooter } from './layout/footerConfigs'
import { SEOHead } from './SEO/SEOHead'
import { getStructuredData } from '../config/structuredData'

export const CareersPage: React.FC = () => {
  const isMobile = useIsMobile()
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' 
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
      : false
  )

  React.useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const { scrollProgress } = useOptimizedScroll()

  return (
    <>
      {/* SEO Head */}
      <SEOHead 
        customTitle="Careers - Mernaki | Join Our Team"
        customDescription="Join the Mernaki team. Explore career opportunities and help us build the future of recruitment technology."
        customMetadata={{
          structuredData: getStructuredData('careers')
        }}
      />

      <motion.div 
        className="min-h-screen relative"
        style={{ 
          backgroundColor: '#F7F5F5',
          borderBottomLeftRadius: '48px',
          borderBottomRightRadius: '48px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden',
          zIndex: 10,
          paddingBottom: '100px',
          transform: `translateY(${scrollProgress * -20}px)`
        }}
        transition={{ duration: 0 }}
      >
        {/* Animated Background Gradient Orbs */}
        <motion.div 
          className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            top: '15%',
            left: '8%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, -80, 60, 0],
            y: [0, 70, -50, 0],
            scale: [1, 1.3, 0.85, 1]
          }}
          transition={{ duration: 27, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-80 h-80 rounded-full opacity-20 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
            top: '50%',
            right: '5%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 90, -70, 0],
            y: [0, -60, 80, 0],
            scale: [1, 0.9, 1.2, 1]
          }}
          transition={{ duration: 32, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div 
          className="absolute w-72 h-72 rounded-full opacity-15 blur-3xl pointer-events-none" 
          style={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            bottom: '15%',
            left: '12%',
            willChange: prefersReducedMotion.current ? 'auto' : 'transform'
          }}
          animate={prefersReducedMotion.current ? {} : {
            x: [0, 70, -90, 0],
            y: [0, -80, 50, 0],
            scale: [1, 1.15, 0.9, 1]
          }}
          transition={{ duration: 29, repeat: Infinity, ease: 'easeInOut' }}
        />

        <HeroSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhoWeAreSection prefersReducedMotion={prefersReducedMotion.current} />
        <DivisionsSection prefersReducedMotion={prefersReducedMotion.current} />
        <WhyWorkHereSection prefersReducedMotion={prefersReducedMotion.current} />
        <OpenRolesSection prefersReducedMotion={prefersReducedMotion.current} />
        <CultureSection prefersReducedMotion={prefersReducedMotion.current} />
      </motion.div>

      <FooterSection isMobile={isMobile} />
    </>
  )
}

const HeroSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  return (
    <section ref={ref} className="relative pt-56 pb-32 overflow-hidden" style={{ backgroundColor: 'transparent' }}>
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.h1 
          className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.03em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Build the Future With Us
        </motion.h1>
        
        <motion.p 
          className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed mb-4" 
          style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 500 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          From advanced software to rare-earth supply, compliance solutions, and next-gen hardware — Mernaki is building a multi-vertical ecosystem.
        </motion.p>
        
        <motion.p 
          className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed mb-12" 
          style={{ color: '#999999', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
        >
          We're looking for curious, ambitious people who want to shape the next era of technology, commerce, and engineering.
        </motion.p>
        
        <motion.div 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <motion.a
            href="#open-roles"
            className="px-8 py-4 rounded-xl text-base inline-block"
            style={{
              backgroundColor: '#1A1A1A',
              color: '#FFFFFF',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none'
            }}
            whileHover={prefersReducedMotion ? {} : { 
              scale: 1.05,
              y: -2,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
            }}
            whileTap={prefersReducedMotion ? {} : { scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333333'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#1A1A1A'}
          >
            View Open Roles
          </motion.a>
        </motion.div>
      </div>
    </section>
  )
}

const WhoWeAreSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-12 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Who We Are
        </motion.h2>
        
        <motion.div 
          className="text-lg md:text-xl leading-relaxed space-y-4" 
          style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <p style={{ color: '#1A1A1A', fontWeight: 500 }}>
            Mernaki is a multi-disciplinary company working across:
          </p>
          <ul className="space-y-3 pl-6">
            <li>• Industrial Supply & Rare Earth Materials</li>
            <li>• Compliance & Data Solutions (VG-Löschung, Documentation, CBAM SaaS)</li>
            <li style={{ filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none', color: 'rgba(102, 102, 102, 0.5)' }}>• Software Engineering (Project Crimson, Project Velocity, Creator Tools)</li>
            <li style={{ filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none', color: 'rgba(102, 102, 102, 0.5)' }}>• Hardware & Product Design (Project Nexus)</li>
            <li>• AI & Automation Platforms</li>
          </ul>
          <p style={{ color: '#1A1A1A', fontWeight: 500, marginTop: '1.5rem' }}>
            Our teams combine creativity, engineering, global trade, and precision German quality.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

const DivisionsSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const divisions = [
    {
      title: 'Software & AI',
      subtitle: 'Project Crimson, Project Velocity, Creator Tools',
      description: 'Build tools that empower developers, gamers, and businesses.',
      isBlurred: true
    },
    {
      title: 'Hardware & Industrial Design',
      subtitle: 'Project Nexus',
      description: 'Craft beautiful, high-performance products with precision.',
      isBlurred: true
    },
    {
      title: 'Rare Earth & Supply Chain',
      subtitle: 'High-Purity Materials',
      description: 'We deliver high-purity materials for advanced industries.',
      isBlurred: false
    },
    {
      title: 'Compliance & Corporate Solutions',
      subtitle: 'VG-Löschung, CBAM',
      description: 'We simplify complex reporting, deletion services, and audits.',
      isBlurred: false
    },
    {
      title: 'Corporate Services',
      subtitle: 'Finance, Sales, Marketing, HR',
      description: 'Support teams that keep our multi-vertical ecosystem running.',
      isBlurred: false
    }
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Our Divisions
        </motion.h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {divisions.map((division, index) => (
            <motion.div
              key={index}
              className="p-8 rounded-2xl transition-all duration-300"
              style={{
                backgroundColor: '#FFFFFF',
                border: '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)'
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 0, 0, 0.1)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)'
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)'
              }}
            >
              <h3 
                className="text-2xl mb-2" 
                style={{ 
                  color: division.isBlurred ? 'rgba(26, 26, 26, 0.3)' : '#1A1A1A', 
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                  fontWeight: 600,
                  filter: division.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {division.title}
              </h3>
              <p 
                className="text-sm mb-3" 
                style={{ 
                  color: division.isBlurred ? 'rgba(74, 144, 226, 0.3)' : '#4A90E2', 
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                  fontWeight: 500,
                  filter: division.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {division.subtitle}
              </p>
              <p 
                className="text-base" 
                style={{ 
                  color: division.isBlurred ? 'rgba(102, 102, 102, 0.3)' : '#666666', 
                  fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                  fontWeight: 400,
                  filter: division.isBlurred ? 'blur(3px)' : 'none'
                }}
              >
                {division.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  )
}

const WhyWorkHereSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const benefits = [
    'Work on high-impact projects across multiple industries',
    'Global supply chain exposure',
    'Cutting-edge software & hardware development',
    'Strong culture of engineering excellence',
    'AI-first development environment',
    'Hybrid remote model',
    'Small team → huge ownership',
    'Work directly with the founder'
  ]
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Why Work at Mernaki
        </motion.h2>
        
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 20 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="flex items-start gap-3 p-4"
              initial={prefersReducedMotion ? { opacity: 1, x: 0 } : { opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 + (index * 0.05), ease: [0.4, 0.0, 0.2, 1] }}
            >
              <div 
                className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                style={{ backgroundColor: 'rgba(74, 144, 226, 0.1)' }}
              >
                <svg className="w-4 h-4" style={{ color: '#4A90E2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p 
                className="text-base" 
                style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
              >
                {benefit}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

const OpenRolesSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.1 })
  
  const roleCategories = [
    {
      category: 'Software Engineering',
      count: 3,
      roles: [
        { title: 'Frontend Engineer (Next.js)', isBlurred: false },
        { title: 'Backend Engineer (Node.js, TypeScript)', isBlurred: false },
        { title: 'AI/ML Engineer', isBlurred: false }
      ]
    },
    {
      category: 'Hardware / Industrial Design',
      count: 1,
      roles: [
        { title: 'Product Designer (Devices)', isBlurred: true }
      ]
    },
    {
      category: 'Supply Chain & Operations',
      count: 2,
      roles: [
        { title: 'Materials Operations Manager', isBlurred: false },
        { title: 'Rare Earth Procurement Analyst', isBlurred: false }
      ]
    },
    {
      category: 'Compliance / Governance',
      count: 1,
      roles: [
        { title: 'CBAM / ESG Analyst', isBlurred: false }
      ]
    },
    {
      category: 'Sales & Partnerships',
      count: 3,
      roles: [
        { title: 'B2B Sales Lead', isBlurred: false },
        { title: 'Partnerships Manager', isBlurred: false },
        { title: 'Enterprise Account Specialist', isBlurred: false }
      ]
    }
  ]
  
  return (
    <section id="open-roles" ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2 
          className="text-4xl md:text-5xl mb-16 text-center" 
          style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, letterSpacing: '-0.02em' }} 
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }} 
          animate={isInView ? { opacity: 1, y: 0 } : {}} 
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          Open Roles
        </motion.h2>
        
        <div className="space-y-8">
          {roleCategories.map((category, catIndex) => (
            <motion.div
              key={catIndex}
              initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: catIndex * 0.1, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <h3 
                className="text-xl mb-4" 
                style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
              >
                {category.category} — ({category.count} {category.count === 1 ? 'role' : 'roles'})
              </h3>
              <div className="space-y-3">
                {category.roles.map((role, roleIndex) => (
                  <a
                    key={roleIndex}
                    href={`mailto:careers@mernaki.de?subject=Application: ${role.title}`}
                    className="block p-5 rounded-xl transition-all duration-300"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      textDecoration: 'none',
                      cursor: role.isBlurred ? 'not-allowed' : 'pointer',
                      pointerEvents: role.isBlurred ? 'none' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                      if (!role.isBlurred) {
                        e.currentTarget.style.transform = 'translateX(8px)'
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.1)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!role.isBlurred) {
                        e.currentTarget.style.transform = 'translateX(0)'
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.05)'
                      }
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <span 
                        className="text-lg" 
                        style={{ 
                          color: role.isBlurred ? 'rgba(26, 26, 26, 0.3)' : '#1A1A1A', 
                          fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", 
                          fontWeight: 500,
                          filter: role.isBlurred ? 'blur(3px)' : 'none'
                        }}
                      >
                        {role.title}
                      </span>
                      {!role.isBlurred && (
                        <svg className="w-5 h-5" style={{ color: '#999999' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
        
        <motion.div
          className="mt-12 text-center"
          initial={prefersReducedMotion ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <a
            href="mailto:careers@mernaki.de?subject=General Application"
            className="px-8 py-4 rounded-xl text-base transition-all duration-300 inline-block"
            style={{
              backgroundColor: 'transparent',
              color: '#1A1A1A',
              border: '2px solid #1A1A1A',
              fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
              fontWeight: 600,
              textDecoration: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1A1A1A'
              e.currentTarget.style.color = '#FFFFFF'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#1A1A1A'
            }}
          >
            Apply Now
          </a>
        </motion.div>
      </div>
    </section>
  )
}

const CultureSection: React.FC<{ prefersReducedMotion: boolean }> = ({ prefersReducedMotion }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  return (
    <section ref={ref} className="relative py-24" style={{ backgroundColor: 'transparent' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="p-12 rounded-3xl"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)'
          }}
          initial={prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <h2 
            className="text-3xl md:text-4xl mb-4" 
            style={{ color: '#1A1A1A', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600 }}
          >
            Small team. Big ambition.
          </h2>
          <p 
            className="text-lg leading-relaxed" 
            style={{ color: '#666666', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}
          >
            At Mernaki, every person has real impact. We move fast, think creatively, and build products that matter across multiple industries. Join us in shaping the future.
          </p>
        </motion.div>
      </div>
    </section>
  )
}

const FooterSection: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: false, amount: 0.3 })
  
  if (isMobile) {
    return <PageMobileFooter {...careersPageFooter} />
  }
  
  return (
    <footer ref={ref} className="relative" style={{ backgroundColor: '#0E0E0E', position: 'sticky', bottom: 0, zIndex: 1, paddingTop: '140px', marginTop: '-80px' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-16">
          {/* Footer Header */}
          <motion.div
            className="mb-16 text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, ease: [0.4, 0.0, 0.2, 1] }}
          >
            <h2 
              className="text-3xl md:text-4xl mb-4"
              style={{ 
                color: '#FFFFFF',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 600,
                letterSpacing: '-0.02em'
              }}
            >
              Join Our Team
            </h2>
            <p 
              className="text-base md:text-lg max-w-2xl mx-auto"
              style={{ 
                color: 'rgba(255, 255, 255, 0.5)',
                fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif",
                fontWeight: 400,
                lineHeight: '1.6'
              }}
            >
              We're building the future across multiple industries. Be part of something exceptional.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
          >
            {/* Column 1 - Products */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Products
              </h3>
              <ul className="space-y-3">
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Software</span></li>
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(2px)', userSelect: 'none', pointerEvents: 'none' }}>Hardware</span></li>
                <li><Link to="/#services" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Services</Link></li>
                <li><Link to="/about" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>About</Link></li>
                <li><Link to="/partnerships" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Partnerships</Link></li>
              </ul>
            </div>

            {/* Column 2 - Company */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Company
              </h3>
              <ul className="space-y-3">
                <li><Link to="/team" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Team</Link></li>
                <li><Link to="/careers" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textDecoration: 'none' }}>Careers</Link></li>
                <li><span className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400 }}>Press</span></li>
              </ul>
            </div>

            {/* Column 3 - Resources */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Resources
              </h3>
              <ul className="space-y-3">
                <li><Link to="/docs" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Documentation</Link></li>
                <li><Link to="/help" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Help Center</Link></li>
                <li><Link to="/status" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>System Status</Link></li>
              </ul>
            </div>

            {/* Column 4 - Legal */}
            <div>
              <h3 className="text-sm mb-4" style={{ color: 'rgba(255, 255, 255, 0.9)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Legal
              </h3>
              <ul className="space-y-3">
                <li><Link to="/privacy-policy" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Terms of Service</Link></li>
                <li><Link to="/impressum" className="text-sm transition-all duration-300" style={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.9)'} onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(255, 255, 255, 0.5)'}>Impressum</Link></li>
              </ul>
            </div>
          </motion.div>

          {/* Showcase Row */}
          <div className="mb-10 pb-10 border-b border-white/10">
            <div className="flex items-center justify-center gap-3 flex-wrap text-sm">
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Crimson</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Velocity</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Creator Tools</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.2)', userSelect: 'none' }}>•</span>
              <span style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 400, filter: 'blur(3px)', userSelect: 'none', pointerEvents: 'none' }}>Project Nexus</span>
            </div>
          </div>

          {/* Bottom Section */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col md:flex-row justify-between items-center gap-6"
          >
            <div className="flex items-center gap-3">
              <img src="/Mernaki_Logo_Pack/transparent/mernaki-128.png" alt="Mernaki Logo" className="object-contain" style={{ height: '42px', width: 'auto', opacity: 0.85, filter: 'brightness(0) invert(1)' }} loading="lazy" />
              <h3 className="text-lg" style={{ color: '#FFFFFF', fontFamily: "'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive", fontWeight: 400 }}>Mernaki</h3>
            </div>
            <p className="text-sm" style={{ color: 'rgba(255, 255, 255, 0.3)', fontFamily: "'Inter', 'Helvetica Neue', 'Arial', sans-serif", fontWeight: 300 }}>© {new Date().getFullYear()} Mernaki • Built in Germany with passion</p>
          </motion.div>
        </div>
      </div>
    </footer>
  )
}
